import { BidderSeatProduct } from '@pubstack/common/src/bidder-seat'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { useSparkStaffAccess } from '~/hooks/useSparkBidder'
import { useSparkForPubStaffAccess } from '~/hooks/useSparkForPub'
import { useScope } from '~/hooks/useScope'
import { ScopeCapabilities } from './bidderRuleSections'

/**
 * Which of the rule's three sections this account can edit.
 *
 * Asks the scope rather than the viewer's features: a Pubstack user has every feature synthesised, so
 * `user.hasFeature` would report Node on a One-only account and open a section holding nothing the
 * customer bought. Node is the one viewer-aware part: admins have it on a staff-only scope.
 */
export const useBidderRuleCapabilities = (): ScopeCapabilities => {
  const scope = useScope()
  const { hasNode } = useNodeStaffAccess()
  // Onboarded and usable by this viewer: staff-only Spark for Pubs reads as off to owners.
  const { canCreateSeat: isSparkForPubAvailable } = useSparkForPubStaffAccess()

  const { hasSpark: isSparkDemandAvailable } = useSparkStaffAccess()

  return {
    hasOne: scope.hasFeature('adstack'),
    // Staff on a scope onboarded on Node but not yet opened to its users edit the server-side section too.
    hasNode,
    isSparkForPubAvailable,
    isSparkDemandAvailable,
  }
}

/**
 * Which product's mount to address on this product-neutral page.
 *
 * Both mounts serve the same collection, so the choice does not change the response — but the page
 * is reachable by a scope on either product, and asking a One-only scope's screen for `/pbs/...`
 * (or the reverse) would read as a Node call in the network tab and in the tracking prefix.
 */
export const useBidderRuleApiProduct = (): BidderSeatProduct => {
  const { hasOne } = useBidderRuleCapabilities()
  return hasOne ? 'adm' : 'pbs'
}
